import React, { useState } from 'react';
import { format, formatDistanceToNowStrict, isToday } from 'date-fns';
import { Checkbox, Skeleton } from 'antd';
import clsx from 'clsx';
import { Conversation } from 'types';
import { useAppSelector } from 'shared/store';
import { selectRealtimeVisitor } from 'shared/store/realtime-slice';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import {
  getVisitorDisplayName,
  getVisitorDomain,
  getVisitorEmail,
} from 'shared/utils/visitor';
import { VisitorAvatar } from 'shared/components/VisitorAvatar';
import { StatusBubble } from 'shared/components/StatusBubble';
import { parseMessage } from 'shared/utils/chat';
import { Icon } from 'shared/components/Icon';
import { CustomTag } from 'browser/components/Dashboard/ClientList/CustomTag';
import './style.less';

interface ConversationItemProps {
  item: Conversation;
  selectedConversations: Record<string, boolean>;
  setSelectedConversations: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export const ConversationItem: React.FC<ConversationItemProps> = ({
  item,
  selectedConversations,
  setSelectedConversations,
}) => {
  const isChecked = !!selectedConversations[item.id];
  const [isHovered, setIsHovered] = useState(false);
  const activeVisitor = useAppSelector(state =>
    selectRealtimeVisitor(state, item.visitor?.id),
  );
  const members = useAppSelector(selectActiveOrgMembers);
  const activeConversationId = useAppSelector(s => s.conversation.conversation?.id);
  const hasUnread = item.unreadMessageCounts.visitor > 0;
  const visitor = activeVisitor || item.visitor;
  const itemTime = item.dateLastMessage || item.dateEnded || item.dateStarted;
  const visitorDisplayName = getVisitorDisplayName(visitor);
  const visitorEmail = getVisitorEmail(visitor);
  const visitorDomain = getVisitorDomain(visitor);
  const visitorLocation = activeVisitor?.location || item.visitor?.lastLocation;

  const firstLine =
    visitorDisplayName && visitorDisplayName !== 'Unknown'
      ? visitorDisplayName
      : visitorEmail
      ? visitorEmail
      : visitorLocation && visitorLocation !== 'Unknown location'
      ? `Unknown in ${visitorLocation}`
      : 'Unknown';
  const secondLine =
    visitorDisplayName && visitorDisplayName !== 'Unknown' && visitorDomain
      ? visitorDomain
      : visitorLocation &&
        visitorLocation !== 'Unknown location' &&
        firstLine !== `Unknown in ${visitorLocation}`
      ? visitorLocation
      : null;

  return (
    <div
      className={clsx(
        'ConversationItem',
        activeConversationId === item.id && 'ConversationItem--active',
      )}
    >
      <div
        className={clsx('ConversationItem__avatar')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isHovered || isChecked ? (
          <div
            className={clsx(
              'ConversationItem__avatar-checkbox',
              isChecked && 'ConversationItem__avatar-checkbox--checked',
            )}
          >
            <Checkbox
              checked={isChecked}
              onChange={e => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedConversations(s => ({ ...s, [item.id]: e.target.checked }));
              }}
            />
          </div>
        ) : (
          <>
            {item.lastMessage?.sourceType === 'agent' ? (
              <div
                className={clsx(
                  'ConversationItem__avatar-replied',
                  activeConversationId === item.id &&
                    'ConversationItem__avatar-replied--active',
                )}
              >
                <Icon icon="reply" />
              </div>
            ) : (
              <VisitorAvatar visitor={visitor} size={40} />
            )}
            <div className="ConversationItem__avatar-bubble">
              <StatusBubble client={visitor} />
            </div>
          </>
        )}
      </div>

      <div className="ConversationItem__visitor">
        <div className="ConversationItem__info">
          {!!visitor?.customMetadata?.tag && (
            <div className="ConversationItem__info-tags">
              <CustomTag tag={visitor?.customMetadata?.tag} />
            </div>
          )}
          <div
            className={clsx(
              'ConversationItem__info-name',
              hasUnread && 'ConversationItem__info-name--unread',
            )}
          >
            {firstLine}
            {secondLine && (
              <span
                className={clsx(
                  'ConversationItem__info-email',
                  hasUnread && 'ConversationItem__info-email--unread',
                )}
              >
                {secondLine}
              </span>
            )}
          </div>
          <div
            className={clsx(
              'ConversationItem__info-date',
              hasUnread && 'ConversationItem__info-date--unread',
            )}
          >
            {isToday(itemTime)
              ? formatDistanceToNowStrict(itemTime, { addSuffix: true }).replace(
                  / hours?| minutes?| seconds?/gi,
                  l => l[1],
                )
              : format(itemTime, 'p')}
          </div>
          {hasUnread && <div className="ConversationItem__info-unread" />}
        </div>

        <div className="ConversationItem__message">
          <div
            className={clsx(
              'ConversationItem__message-inner',
              (hasUnread || activeConversationId === item.id) &&
                'ConversationItem__message-inner--unread',
            )}
          >
            {item.lastMessage?.sourceType === 'agent' &&
              `${item.lastMessage.agent.name.split(' ')[0]}: `}
            {parseMessage(item.lastMessage?.text || '', members, true)}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FakeConversationItem: React.FC<{ loading?: boolean }> = ({ loading }) => {
  return (
    <div className="ConversationItem ConversationItem--fake">
      <div className="ConversationItem__avatar">
        <Skeleton
          avatar={{ size: 40 }}
          title={false}
          paragraph={false}
          active={loading}
        />
        <div className="ConversationItem__avatar-bubble">
          <StatusBubble client={undefined} />
        </div>
      </div>
      <div className="ConversationItem__skeleton">
        <Skeleton
          paragraph={{ rows: 2, width: ['40%', '50%'] }}
          title={false}
          active={loading}
        />
      </div>
    </div>
  );
};
